import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { useSeasonalTheme } from '../contexts/SeasonalThemeContext';
import logo from '../assets/logo.png';

const Nav = styled.nav`
  position: fixed;
  top: ${props => props.$bannerOffset ? '28px' : '0'};
  width: 100%;
  background: var(--nav-bg);
  backdrop-filter: blur(10px);
  z-index: 1000;
  transition: all 0.3s ease, transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 20px var(--nav-shadow);
  transform: translateY(${props => props.$hide ? '-100%' : '0'});
`;

const NavContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 20px;
`;

const NavLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

const Logo = styled.img`
  height: 50px;
  width: 50px;
  object-fit: cover;
  border-radius: 50%;
  border: 2px solid var(--brand-primary);
  box-shadow: 0 2px 8px var(--brand-shadow);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px var(--brand-shadow);
    border-color: var(--brand-secondary);
  }

  @media (max-width: 480px) {
    height: 40px;
    width: 40px;
  }
`;

const LogoText = styled.span`
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--brand-primary);
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }

  @media (max-width: 480px) {
    font-size: 1.1rem;
    white-space: normal;
    line-height: 1.2;
  }

  @media (max-width: 380px) {
    display: none;
  }
`;

const NavMenu = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;

  @media (max-width: 768px) {
    position: fixed;
    inset: 0;
    flex-direction: column;
    background-color: var(--nav-mobile-bg);
    width: 100%;
    height: 100vh;
    height: 100dvh;
    text-align: center;
    justify-content: center;
    gap: 0;
    padding: 0 2rem;
    z-index: 999;
    opacity: ${props => props.$isOpen ? '1' : '0'};
    visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
    transition: opacity 0.35s ease, visibility 0.35s ease;

    & > button {
      opacity: ${props => props.$isOpen ? '1' : '0'};
      transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(20px)'};
    }
  }
`;

const NavLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  text-decoration: none;
  color: var(--text-primary);
  font-weight: 500;
  font-size: inherit;
  font-family: inherit;
  transition: color 0.3s ease;
  position: relative;
  cursor: pointer;

  &::after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: -5px;
    left: 0;
    background: var(--brand-primary);
    transition: width 0.3s ease;
  }

  &:hover,
  &:focus-visible {
    color: var(--brand-primary);
  }

  &:hover::after,
  &:focus-visible::after {
    width: 100%;
  }

  &:focus-visible {
    outline: 2px solid var(--brand-primary);
    outline-offset: 4px;
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-family: 'Playfair Display', serif;
    font-size: 1.8rem;
    font-weight: 600;
    padding: 0.8rem 0;
    color: var(--text-primary);
    letter-spacing: 0.5px;
    transform: translateY(20px);
    opacity: 0;
    transition: opacity 0.35s ease, transform 0.35s ease, color 0.3s ease;
    transition-delay: calc(var(--nav-index, 0) * 0.06s);

    &::after {
      bottom: 4px;
      height: 2px;
      left: 50%;
      transform: translateX(-50%);
    }

    &:hover::after,
    &:focus-visible::after {
      width: 40px;
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;

const LanguageSwitcher = styled.div`
  display: flex;
  gap: 5px;
  margin-left: 1rem;

  @media (max-width: 768px) {
    margin-left: 0;
    margin-top: 2rem;
    justify-content: center;
    gap: 8px;
  }
`;

const LangBtn = styled.button`
  background: transparent;
  border: 2px solid var(--brand-primary);
  color: var(--brand-primary);
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;

  &:hover {
    background: var(--brand-primary);
    color: var(--text-inverse);
  }

  &.active {
    background: var(--brand-primary);
    color: var(--text-inverse);
  }
`;

const ThemeToggleBtn = styled.button`
  background: transparent;
  border: 2px solid var(--brand-primary);
  color: var(--brand-primary);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  padding: 0;
  line-height: 1;

  &:hover {
    background: var(--brand-primary);
    color: var(--text-inverse);
  }

  &:active {
    transform: rotate(180deg);
  }
`;

const Hamburger = styled.div`
  display: none;
  flex-direction: column;
  cursor: pointer;
  z-index: 1001;
  width: 28px;
  height: 22px;
  position: relative;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const Bar = styled.span`
  width: 25px;
  height: 2.5px;
  background: var(--text-primary);
  border-radius: 2px;
  position: absolute;
  left: 0;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);

  &:nth-child(1) {
    top: ${props => props.$isOpen ? '10px' : '0'};
    transform: ${props => props.$isOpen ? 'rotate(45deg)' : 'rotate(0)'};
  }
  &:nth-child(2) {
    top: 9px;
    opacity: ${props => props.$isOpen ? '0' : '1'};
    transform: ${props => props.$isOpen ? 'translateX(10px)' : 'translateX(0)'};
  }
  &:nth-child(3) {
    top: ${props => props.$isOpen ? '10px' : '18px'};
    transform: ${props => props.$isOpen ? 'rotate(-45deg)' : 'rotate(0)'};
  }
`;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hideNav, setHideNav] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const { currentLanguage, switchLanguage, t } = useLanguage();
  const { isDark, themeMode, setTheme } = useTheme();
  const { activeTheme, avatarUrl } = useSeasonalTheme();

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (!isOpen) {
        if (currentScrollY > 100) {
          setHideNav(currentScrollY > lastScrollY && currentScrollY - lastScrollY > 5);
        } else {
          setHideNav(false);
        }
      }

      setLastScrollY(currentScrollY);

      const navbar = document.querySelector('nav');
      if (currentScrollY > 50) {
        navbar.style.boxShadow = '0 2px 20px var(--nav-shadow-scrolled)';
      } else {
        navbar.style.boxShadow = '0 2px 20px var(--nav-shadow)';
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, isOpen]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offsetTop = element.offsetTop - (activeTheme ? 108 : 80);
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
    setIsOpen(false);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    setIsOpen(false);
  };

  const cycleTheme = () => {
    const modes = ['system', 'light', 'dark'];
    const next = modes[(modes.indexOf(themeMode) + 1) % modes.length];
    setTheme(next);
  };

  return (
    <Nav $hide={hideNav} $bannerOffset={!!activeTheme}>
      <NavContainer>
        <NavLogo onClick={scrollToTop} role="button" tabIndex={0} aria-label={t('Go to top', 'Về đầu trang')} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); scrollToTop(); } }}>
          <Logo src={activeTheme && avatarUrl ? avatarUrl : logo} alt="Hoang Uyen Da Lat Homestay" />
          <LogoText>
            {t("Hoang Uyen Da Lat Homestay", "Hoàng Uyên Đà Lạt Homestay")}
          </LogoText>
        </NavLogo>

        <NavMenu $isOpen={isOpen} role="navigation" aria-label={t('Main navigation', 'Điều hướng chính')}>
          <NavLink onClick={() => scrollToSection('home')} style={{ '--nav-index': 0 }}>
            {t("Home", "Trang chủ")}
          </NavLink>
          <NavLink onClick={() => scrollToSection('rooms')} style={{ '--nav-index': 1 }}>
            {t("Rooms", "Phòng")}
          </NavLink>
          <NavLink onClick={() => scrollToSection('activities')} style={{ '--nav-index': 2 }}>
            {t("Activities", "Hoạt động")}
          </NavLink>
          <NavLink onClick={() => scrollToSection('contact')} style={{ '--nav-index': 3 }}>
            {t("Contact", "Liên hệ")}
          </NavLink>

          <LanguageSwitcher style={{ '--nav-index': 4 }}>
            <LangBtn
              className={currentLanguage === 'en' ? 'active' : ''}
              onClick={() => switchLanguage('en')}
              aria-label="Switch to English"
              aria-pressed={currentLanguage === 'en'}
            >
              EN
            </LangBtn>
            <LangBtn
              className={currentLanguage === 'vi' ? 'active' : ''}
              onClick={() => switchLanguage('vi')}
              aria-label="Chuyển sang Tiếng Việt"
              aria-pressed={currentLanguage === 'vi'}
            >
              VI
            </LangBtn>
            <ThemeToggleBtn
              onClick={cycleTheme}
              title={`Theme: ${themeMode}`}
              aria-label={t(
                `Switch theme (current: ${themeMode})`,
                `Đổi giao diện (hiện tại: ${themeMode})`
              )}
            >
              {themeMode === 'system' ? '💻' : isDark ? '☀️' : '🌙'}
            </ThemeToggleBtn>
          </LanguageSwitcher>
        </NavMenu>

        <Hamburger
          onClick={() => setIsOpen(!isOpen)}
          role="button"
          tabIndex={0}
          aria-label={isOpen ? t('Close menu', 'Đóng menu') : t('Open menu', 'Mở menu')}
          aria-expanded={isOpen}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsOpen(!isOpen); } }}
        >
          <Bar $isOpen={isOpen} />
          <Bar $isOpen={isOpen} />
          <Bar $isOpen={isOpen} />
        </Hamburger>
      </NavContainer>
    </Nav>
  );
};

export default Navbar;
